import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authService, userService } from "../services/service";

const AuthContext = createContext(null);
let cachedSession = null;
let sessionRequest = null;
const profileCache = new Map();
const profileRequests = new Map();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(cachedSession?.user ?? null);
  const [permissions, setPermissions] = useState(cachedSession?.permissions ?? []);
  const [userAccount, setUserAccount] = useState(null);
  const [loading, setLoading] = useState(!cachedSession);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(cachedSession?.user));
  const [isInitialized, setIsInitialized] = useState(Boolean(cachedSession));

  const clearSession = useCallback(() => {
    cachedSession = null;
    sessionRequest = null;
    setUser(null);
    setPermissions([]);
    setUserAccount(null);
    setIsAuthenticated(false);
  }, []);

  const checkAuth = useCallback(async (force = false) => {
    if (!force && cachedSession) {
      setUser(cachedSession.user);
      setPermissions(cachedSession.permissions ?? []);
      setIsAuthenticated(true);
      setLoading(false);
      setIsInitialized(true);
      return cachedSession.user;
    }

    try {
      setLoading(true);
      setError(null);
      if (!sessionRequest) sessionRequest = authService.me();
      const response = await sessionRequest;

      const authenticatedUser = response?.data?.user ?? null;
      const userPermissions = response?.data?.permissions ?? [];

      if (authenticatedUser) {
        cachedSession = { user: authenticatedUser, permissions: userPermissions };
        setUser(authenticatedUser);
        setPermissions(userPermissions);
        setIsAuthenticated(true);
      } else {
        clearSession();
      }
      return authenticatedUser;
    } catch (err) {
      clearSession();
      setError(err);
      throw err;
    } finally {
      sessionRequest = null;
      setLoading(false);
      setIsInitialized(true);
    }
  }, [clearSession]);

  const refreshUserAccount = useCallback(async (username, force = false) => {
    const accountUsername = username ?? user?.username;
    if (!accountUsername) return null;
    if (!force && profileCache.has(accountUsername)) {
      const account = profileCache.get(accountUsername);
      setUserAccount(account);
      return account;
    }
    try {
      if (!profileRequests.has(accountUsername)) {
        profileRequests.set(accountUsername, userService.getByUsername(accountUsername));
      }
      const response = await profileRequests.get(accountUsername);
      const account = response?.data ?? null;
      profileCache.set(accountUsername, account);
      setUserAccount(account);
      return account;
    } finally {
      profileRequests.delete(accountUsername);
    }
  }, [user?.username]);

  useEffect(() => {
    checkAuth().catch(() => {});
  }, [checkAuth]);

  useEffect(() => {
    if (user?.username) refreshUserAccount().catch(() => setUserAccount(null));
  }, [user?.username, refreshUserAccount]);

  const login = useCallback(async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.login(credentials);

      const loggedInUser = response?.data?.user ?? null;
      const userPermissions = response?.data?.permissions ?? [];

      if (loggedInUser) {
        cachedSession = { user: loggedInUser, permissions: userPermissions };
        setUser(loggedInUser);
        setPermissions(userPermissions);
        setIsAuthenticated(true);
      }

      await checkAuth(true);
      return response;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  const register = useCallback((data) => authService.register(data), []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      clearSession();
      profileCache.clear();
      window.location.href = "/login";
    }
  }, [clearSession]);

  const refresh = useCallback(async () => {
    const response = await authService.refresh();
    await checkAuth(true);
    return response;
  }, [checkAuth]);

  const changePassword = useCallback((data) => authService.changePassword(data), []);

  const forgotPassword = useCallback((data) => authService.forgotPassword(data), []);
  const verifyOtp      = useCallback((data) => authService.verifyOtp(data),      []);
  const resetPassword  = useCallback((data) => authService.resetPassword(data),  []);

  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    if (!Array.isArray(permissions) || permissions.length === 0) return false;
    if (permissions.includes("manage_all")) return true;

    const required = Array.isArray(permission) ? permission : [permission];
    return required.some((p) => permissions.includes(p));
  }, [permissions]);

  const hasAllPermissions = useCallback((perms) => {
    if (!Array.isArray(perms) || perms.length === 0) return true;
    if (!Array.isArray(permissions) || permissions.length === 0) return false;
    if (permissions.includes("manage_all")) return true;
    return perms.every((p) => permissions.includes(p));
  }, [permissions]);

  const value = useMemo(() => ({
    user, userAccount, permissions, loading, error, isAuthenticated, isInitialized,
    login, register, logout, refresh, changePassword, checkAuth, refreshUserAccount,
    hasPermission, hasAllPermissions,
    forgotPassword, verifyOtp, resetPassword,
  }), [user, userAccount, permissions, loading, error, isAuthenticated, isInitialized,
      login, register, logout, refresh, changePassword, checkAuth, refreshUserAccount,
      hasPermission, hasAllPermissions,
      forgotPassword, verifyOtp, resetPassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};